import type { FamilySeatView, FamilyView } from "@keydials/api-client";
import { formatSubscriptionDate } from "./subscriptionSummary";

/**
 * What the Family card says (KEY-651), worked out here so the words can be tested without a browser. The
 * server decides everything — who holds a place, whether another invitation can go; this only says it.
 */

/** "2 of 3 places used". */
export function placesLabel(family: FamilyView): string {
  return `${family.seatsUsed} of ${family.seatsTotal} ${family.seatsTotal === 1 ? "place" : "places"} used`;
}

/** One relative's line under their name. */
export function seatStatusLabel(seat: FamilySeatView): string {
  switch (seat.status) {
    case "Joined":
      return seat.joinedAtUtc ? `Joined ${formatSubscriptionDate(seat.joinedAtUtc)}` : "Joined";
    case "Expired":
      return "Invitation expired — send it again to give them a new link";
    default:
      return seat.expiresAtUtc
        ? `Invited — the link works until ${formatSubscriptionDate(seat.expiresAtUtc)}`
        : "Invited";
  }
}

/** Whether an invitation can be sent again: not for somebody who has joined — there is nothing left to send. */
export function isPending(seat: FamilySeatView): boolean {
  return seat.status !== "Joined";
}

/** The button that frees a place: withdrawing an invitation, or removing somebody who joined (phase 2). */
export function freeLabel(seat: FamilySeatView): string {
  return seat.status === "Joined" ? "Remove" : "Withdraw";
}

/** What freeing the place does, asked before it happens. */
export function freeConfirm(seat: FamilySeatView): string {
  return seat.status === "Joined"
    ? `Remove ${seat.name} from your family plan? Their dial pauses — nothing in it is deleted — and they're offered to carry on by paying for it themselves. Their place is freed for somebody else.`
    : `Withdraw the invitation to ${seat.name}? The link they were sent will stop working.`;
}

/** What to say once the place is free. */
export function freedNote(seat: FamilySeatView): string {
  return seat.status === "Joined"
    ? `${seat.name} has been removed, and we've let them know.`
    : `Invitation to ${seat.name} withdrawn.`;
}

/** What to say after an invitation went out, or failed to. */
export function sentNote(email: string, emailSent: boolean): string {
  return emailSent
    ? `Invitation sent to ${email}.`
    : `The invitation is saved, but the email to ${email} didn't send. Use "Send again" to try once more.`;
}
